import { useState, useEffect, useRef } from "react";
import {
  quizQuestions, serviceModules, menuSections,
  wineProgram, wineFoundations, spiritsFoundations, spiritsProgram, signatureCocktails,
  cocktailPairings, winePairings, languageStandards
} from "./data.js";

const COLORS = {
  bg: "#04070D",
  card: "#0F1826",
  cardHover: "#132030",
  gold: "#8FAFD4",
  goldLight: "#A9C4E2",
  goldDim: "#6E8DB3",
  cream: "#EDF2F8",
  creamDim: "#C9D6E4",
  green: "#8FAFD4",
  greenLight: "#A9C4E2",
  greenSoft: "#101B2A",
  red: "#C25555",
  redLight: "#E07070",
  redSoft: "#2A1216",
  white: "#0F1826",
  border: "#1D2C40",
  borderDark: "#2A3D58",
  textPrimary: "#EDF2F8",
  textSecondary: "#C9D6E4",
  textMuted: "#8595AA",
  headerBg: "#04070D",
  accentBg: "#0B1420",
};

function AskTab() {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const askQuestion = async () => {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setQuestion("");
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setLoading(true);

    try {
      const response = await fetch("/api/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q }),
      });
      const data = await response.json();
      const answer = data.answer || "Sorry, I couldn't find an answer. Try rephrasing your question.";
      setMessages((prev) => [...prev, { role: "assistant", text: answer }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: "assistant", text: "Something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "What is the black cod marinated in?",
    "How should I greet a table?",
    "How do I introduce a wine at the table?",
    "What do I do if a guest sends back wine?",
    "What are the sake grades?",
    "How should I present dessert?",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 240px)", minHeight: 500 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>AI Training Assistant</div>
        <h1 style={{ fontSize: 36, fontWeight: 400, color: COLORS.textPrimary, margin: "0 0 8px", fontFamily: "'Cormorant Garamond', serif" }}>Ask a Question</h1>
        <p style={{ fontSize: 14, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, margin: 0 }}>
          Ask anything about Zuma service standards, menu items, ingredients, or protocols.
        </p>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, marginBottom: 20, paddingRight: 4 }}>
        {messages.length === 0 && !loading && (
          <div style={{ padding: "32px 0" }}>
            <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted, marginBottom: 16, fontWeight: 400 }}>Try asking about:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => { setQuestion(s); }} style={{
                  padding: "10px 16px", background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20,
                  fontSize: 13, fontFamily: "'Outfit', sans-serif", color: COLORS.textSecondary, cursor: "pointer",
                  transition: "all 0.2s", fontWeight: 400,
                }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.goldLight; e.currentTarget.style.background = COLORS.cardHover; }}
                   onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.background = COLORS.white; }}
                >{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "80%", padding: "14px 18px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
              background: msg.role === "user" ? COLORS.gold : COLORS.white,
              color: msg.role === "user" ? COLORS.bg : COLORS.textPrimary,
              border: msg.role === "user" ? "none" : `1px solid ${COLORS.border}`,
              fontSize: 14, fontFamily: "'Outfit', sans-serif", fontWeight: 300, lineHeight: 1.65,
              whiteSpace: "pre-wrap",
            }}>
              {msg.role === "assistant" && (
                <div style={{ fontSize: 10, letterSpacing: 2, color: COLORS.goldDim, textTransform: "uppercase", marginBottom: 8, fontWeight: 500 }}>Zuma Training Assistant</div>
              )}
              {msg.text}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "14px 18px", borderRadius: "18px 18px 18px 4px", background: COLORS.white, border: `1px solid ${COLORS.border}` }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: COLORS.goldDim, textTransform: "uppercase", marginBottom: 8, fontWeight: 500 }}>Zuma Training Assistant</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {[0, 1, 2].map((d) => (
                  <div key={d} style={{
                    width: 7, height: 7, borderRadius: "50%", background: COLORS.goldDim,
                    animation: "pulse 1.2s ease-in-out infinite", animationDelay: `${d * 0.2}s`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "6px 6px 6px 18px" }}>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && askQuestion()}
          placeholder="Ask about service standards, menu items, or protocols..."
          style={{
            flex: 1, border: "none", outline: "none", fontSize: 14, fontFamily: "'Outfit', sans-serif",
            color: COLORS.textPrimary, background: "transparent", fontWeight: 400,
          }}
        />
        <button onClick={askQuestion} disabled={!question.trim() || loading} style={{
          padding: "10px 22px", background: question.trim() && !loading ? COLORS.gold : COLORS.border,
          border: "none", borderRadius: 8, color: question.trim() && !loading ? COLORS.bg : COLORS.textMuted,
          fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 500, cursor: question.trim() && !loading ? "pointer" : "default",
          transition: "all 0.2s", letterSpacing: 0.5,
        }}>Ask</button>
      </div>

      <style>{`@keyframes pulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
}

const ACCESS_CODE = "KOUGL2026";

function AccessGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (code.trim().toUpperCase() === ACCESS_CODE) {
      sessionStorage.setItem("zuma_access", "1");
      onUnlock();
    } else {
      setError(true);
      setCode("");
    }
  };
  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(rgba(4,7,13,0.55), rgba(4,7,13,0.75)), url('/zuma-bg.jpeg') center/cover no-repeat fixed, ${COLORS.bg}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", padding: 24 }}>
      <div style={{ background: "rgba(9, 15, 26, 0.82)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", border: `1px solid ${COLORS.goldDim}`, borderRadius: 14, padding: "52px 44px", maxWidth: 440, width: "100%", textAlign: "center", boxShadow: "0 24px 70px rgba(0,0,0,0.65)" }}>
        <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 48, color: COLORS.gold, margin: "0 0 6px", fontWeight: 700, letterSpacing: 16, textTransform: "lowercase", textShadow: "0 2px 20px rgba(143,175,212,0.35)" }}>zuma</h1>
        <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, letterSpacing: 5, color: COLORS.goldDim, marginBottom: 32 }}>STAFF TRAINING</div>
        <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${COLORS.gold}, transparent)`, opacity: 0.5, margin: "0 auto 32px", width: 140 }} />
        <p style={{ color: COLORS.gold, fontSize: 17, marginBottom: 10, letterSpacing: 1, fontWeight: 500 }}>Welcome to the zuma family</p>
        <p style={{ color: COLORS.creamDim, fontSize: 14, marginBottom: 24, letterSpacing: 0.5 }}>Please enter your access code to continue.</p>
        <form onSubmit={submit}>
          <input
            type="password"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(false); }}
            placeholder="Access code"
            autoFocus
            style={{
              width: "100%", padding: "14px 16px", fontSize: 16, textAlign: "center", letterSpacing: 4,
              background: COLORS.bg, color: COLORS.cream, border: `1px solid ${error ? COLORS.red : COLORS.goldDim}`,
              borderRadius: 6, outline: "none", boxSizing: "border-box", fontFamily: "'Outfit', sans-serif",
              textTransform: "uppercase",
            }}
          />
          {error && <div style={{ color: COLORS.red, fontSize: 13, marginTop: 12 }}>Incorrect code. Please try again.</div>}
          <button
            type="submit"
            style={{
              marginTop: 20, width: "100%", padding: "14px", fontSize: 14, letterSpacing: 3,
              background: COLORS.gold, color: COLORS.bg, border: "none", borderRadius: 6,
              cursor: "pointer", fontWeight: 600, fontFamily: "'Outfit', sans-serif",
            }}
          >
            ENTER
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [unlocked, setUnlocked] = useState(() => typeof window !== "undefined" && sessionStorage.getItem("zuma_access") === "1");
  const [activeTab, setActiveTab] = useState("home");
  const [quizState, setQuizState] = useState({ started: false, current: 0, score: 0, answers: [], finished: false, pool: [] });
  const [expandedModule, setExpandedModule] = useState(null);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [expandedWine, setExpandedWine] = useState(null);
  const [wineSubTab, setWineSubTab] = useState("wine");
  const [fadeIn, setFadeIn] = useState(true);

  useEffect(() => {
    setFadeIn(false);
    const t = setTimeout(() => setFadeIn(true), 50);
    return () => clearTimeout(t);
  }, [activeTab]);

  const startQuiz = (type) => {
    let pool;
    if (type === "service") pool = quizQuestions.filter((q) => q.section === "Service" || q.section === "Reception");
    else if (type === "menu") pool = quizQuestions.filter((q) => q.section === "Menu");
    else if (type === "wine") pool = quizQuestions.filter((q) => q.section === "Wine & Spirits");
    else pool = [...quizQuestions];
    if (pool.length === 0) return;
    const shuffled = pool
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.min(15, pool.length))
      .map((q) => {
        const order = q.options.map((_, i) => i).sort(() => Math.random() - 0.5);
        return { ...q, options: order.map((i) => q.options[i]), answer: order.indexOf(q.answer) };
      });
    setQuizState({ started: true, current: 0, score: 0, answers: [], finished: false, pool: shuffled });
    setActiveTab("quiz");
  };

  const answerQuestion = (idx) => {
    if (quizState.answers.length > quizState.current) return;
    if (!quizState.pool[quizState.current]) return;
    const correct = idx === quizState.pool[quizState.current].answer;
    setQuizState({
      ...quizState,
      score: quizState.score + (correct ? 1 : 0),
      answers: [...quizState.answers, { chosen: idx, correct }],
    });
  };

  const nextQuestion = () => {
    if (quizState.pool.length === 0) return;
    const isLast = quizState.current >= quizState.pool.length - 1;
    setQuizState({ ...quizState, current: isLast ? quizState.current : quizState.current + 1, finished: isLast });
  };

  const tabs = [
    { id: "home", label: "Home" },
    { id: "service", label: "Service Standards" },
    { id: "menu", label: "Menu Knowledge" },
    { id: "wines", label: "Wine & Spirits" },
    { id: "language", label: "Language Guide" },
    { id: "quiz", label: "Knowledge Check" },
    { id: "ask", label: "Ask a Question" },
  ];

  if (!unlocked) {
    return (
      <>
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <AccessGate onUnlock={() => setUnlocked(true)} />
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, color: COLORS.textPrimary, fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Header */}
      <header style={{ borderBottom: `1px solid ${COLORS.border}`, padding: "16px 0", position: "sticky", top: 0, background: COLORS.headerBg, zIndex: 100, backdropFilter: "blur(12px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 22, letterSpacing: 8, color: COLORS.gold, fontFamily: "'Outfit', sans-serif", fontWeight: 700, textTransform: "lowercase" }}>zuma</div>
            <div>
              <div style={{ fontSize: 12, letterSpacing: 3, color: COLORS.gold, fontFamily: "'Outfit', sans-serif", fontWeight: 500, textTransform: "uppercase" }}>Abu Dhabi</div>
              <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.textMuted, fontFamily: "'Outfit', sans-serif", fontWeight: 300, textTransform: "uppercase", marginTop: 1 }}>Staff Training Program</div>
            </div>
          </div>
          <nav style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                padding: "7px 14px", fontSize: 11, letterSpacing: 1.2, fontFamily: "'Outfit', sans-serif", fontWeight: activeTab === t.id ? 500 : 400,
                background: activeTab === t.id ? COLORS.gold : "transparent", color: activeTab === t.id ? COLORS.bg : COLORS.textSecondary,
                border: `1px solid ${activeTab === t.id ? COLORS.gold : "transparent"}`, borderRadius: 6, cursor: "pointer", textTransform: "uppercase",
                transition: "all 0.2s ease",
              }} onMouseEnter={(e) => { if (activeTab !== t.id) e.currentTarget.style.background = COLORS.cardHover; }}
                 onMouseLeave={(e) => { if (activeTab !== t.id) e.currentTarget.style.background = "transparent"; }}
              >{t.label}</button>
            ))}
          </nav>
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px", opacity: fadeIn ? 1 : 0, transition: "opacity 0.3s ease" }}>

        {/* HOME */}
        {activeTab === "home" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div style={{ fontSize: 11, letterSpacing: 6, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 16 }}>Confidential — For Staff Use Only</div>
              <h1 style={{ fontSize: "clamp(32px, 5vw, 50px)", fontWeight: 300, color: COLORS.textPrimary, lineHeight: 1.2, margin: "0 0 16px" }}>Staff Training Program</h1>
              <p style={{ fontSize: 20, fontStyle: "italic", color: COLORS.goldDim, fontWeight: 400, maxWidth: 560, margin: "0 auto" }}>
                "Zuma — a move away from the traditional. Authentic, but not traditional."
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginBottom: 40 }}>
              {[
                { icon: "◆", title: "Service Standards", desc: "The Zuma story, izakaya culture, the venue and layout, guest arrival, service flow, wine service, and the allergy protocol.", tab: "service" },
                { icon: "◇", title: "Menu Knowledge", desc: "Every section of the menu — snacks, salads, tempura, sushi and maki, the robata grill, signatures, desserts, allergens, and Japanese vocabulary.", tab: "menu" },
                { icon: "✧", title: "Wine & Spirits", desc: "Sake foundations and grades, wine service standards, shochu and Japanese whisky, the four signature cocktails, and pairing guides for every dish.", tab: "wines" },
                { icon: "❖", title: "Language Guide", desc: "Approved verbiage for every service moment — greeting through farewell, plus recovery scripts.", tab: "language" },
                { icon: "◈", title: "Knowledge Check", desc: "Test your readiness with quizzes covering service, menu, wine, spirits, and reception.", tab: "quiz" },
                { icon: "✦", title: "Ask a Question", desc: "Type any question about Zuma standards, menu, or protocols and get an instant answer.", tab: "ask" },
              ].map((card) => (
                <button key={card.tab} onClick={() => setActiveTab(card.tab)} style={{
                  background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "28px 24px", textAlign: "left", cursor: "pointer",
                  transition: "all 0.25s ease", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.goldLight; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
                   onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                >
                  <span style={{ fontSize: 22, color: COLORS.gold }}>{card.icon}</span>
                  <span style={{ fontSize: 19, fontWeight: 500, color: COLORS.textPrimary }}>{card.title}</span>
                  <span style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.5 }}>{card.desc}</span>
                </button>
              ))}
            </div>

            <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "28px 32px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              <h2 style={{ fontSize: 20, fontWeight: 500, color: COLORS.gold, margin: "0 0 16px" }}>Quick Start — Take a Quiz</h2>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  { label: "Service Standards Quiz", type: "service" },
                  { label: "Menu Knowledge Quiz", type: "menu" },
                  { label: "Wine & Spirits Quiz", type: "wine" },
                  { label: "Full Training Quiz", type: "all" },
                ].map((q) => (
                  <button key={q.type} onClick={() => startQuiz(q.type)} style={{
                    padding: "11px 22px", background: COLORS.gold, border: "none", borderRadius: 7,
                    color: COLORS.bg, fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 500, cursor: "pointer",
                    letterSpacing: 1, textTransform: "uppercase", transition: "all 0.2s",
                  }} onMouseEnter={(e) => e.currentTarget.style.background = COLORS.goldLight}
                     onMouseLeave={(e) => e.currentTarget.style.background = COLORS.gold}
                  >{q.label}</button>
                ))}
              </div>
            </div>

            <div style={{ background: COLORS.greenSoft, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "24px 28px" }}>
              <div style={{ fontSize: 11, letterSpacing: 3, color: COLORS.green, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8, fontWeight: 500 }}>★ The Zuma Standard</div>
              <p style={{ fontSize: 17, fontStyle: "italic", color: COLORS.textPrimary, fontWeight: 400, margin: 0, lineHeight: 1.6 }}>
                Our purpose is simple: authentic but not traditional. Share generously, serve continuously, and make every guest leave wanting to tell someone about it — positive word of mouth and loyalty are the ultimate measure.
              </p>
            </div>
          </div>
        )}

        {/* SERVICE STANDARDS */}
        {activeTab === "service" && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>Reference Guide</div>
              <h1 style={{ fontSize: 34, fontWeight: 400, color: COLORS.textPrimary, margin: 0 }}>Service Standards</h1>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {serviceModules.map((mod) => (
                <div key={mod.id} style={{ background: COLORS.white, border: `1px solid ${expandedModule === mod.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <button onClick={() => setExpandedModule(expandedModule === mod.id ? null : mod.id)} style={{
                    width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", gap: 14, background: "transparent", border: "none",
                    cursor: "pointer", textAlign: "left", color: COLORS.textPrimary,
                  }}>
                    <span style={{ fontSize: 18, color: COLORS.gold, width: 26, textAlign: "center" }}>{mod.icon}</span>
                    <span style={{ fontSize: 18, fontWeight: 500, flex: 1 }}>{mod.title}</span>
                    <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedModule === mod.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                  </button>
                  {expandedModule === mod.id && (
                    <div style={{ padding: "0 22px 22px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                      {mod.items.map((item, i) => (
                        <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "16px 18px" }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold, marginBottom: 6 }}>{item.label}</div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{item.desc}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MENU KNOWLEDGE */}
        {activeTab === "menu" && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>Reference Guide</div>
              <h1 style={{ fontSize: 34, fontWeight: 400, color: COLORS.textPrimary, margin: 0 }}>Menu Knowledge</h1>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {menuSections.map((sec) => (
                <div key={sec.id} style={{ background: COLORS.white, border: `1px solid ${expandedMenu === sec.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <button onClick={() => setExpandedMenu(expandedMenu === sec.id ? null : sec.id)} style={{
                    width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent",
                    border: "none", cursor: "pointer", color: COLORS.textPrimary, textAlign: "left",
                  }}>
                    <span style={{ fontSize: 18, fontWeight: 500 }}>{sec.title}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}>{sec.items.length} items</span>
                      <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedMenu === sec.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                    </div>
                  </button>
                  {expandedMenu === sec.id && (
                    <div style={{ padding: "0 22px 22px", display: "grid", gap: 8 }}>
                      {sec.items.map((item, i) => (
                        <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.gold }}>{item.name}</span>
                            <span style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.green, background: COLORS.greenSoft, padding: "3px 10px", borderRadius: 20, letterSpacing: 1, textTransform: "uppercase" }}>{item.pairing}</span>
                          </div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{item.desc}</div>
                          {item.allergens && (
                            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                              <span style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.redLight, letterSpacing: 1, textTransform: "uppercase" }}>Allergens:</span>
                              {item.allergens.split(", ").map((a, j) => (
                                <span key={j} style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.redLight, background: COLORS.redSoft, padding: "2px 8px", borderRadius: 12, letterSpacing: 0.5 }}>{a}</span>
                              ))}
                            </div>
                          )}
                          {item.verbiage && (
                            <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: `2px solid ${COLORS.goldDim}44`, fontSize: 13, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", color: COLORS.textMuted, lineHeight: 1.5 }}>
                              "{item.verbiage}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WINE & SPIRITS */}
        {activeTab === "wines" && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>Reference Guide</div>
              <h1 style={{ fontSize: 34, fontWeight: 400, color: COLORS.textPrimary, margin: "0 0 16px" }}>Wine & Spirits Program</h1>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { id: "foundations", label: "Sake & Wine Foundations" },
                  { id: "wine", label: "Sake & Wine Program" },
                  { id: "spiritsFoundations", label: "Spirits Foundations" },
                  { id: "spirits", label: "Spirits" },
                  { id: "cocktails", label: "Signature Cocktails" },
                  { id: "pairings", label: "Pairing Guides" },
                ].map((st) => (
                  <button key={st.id} onClick={() => { setWineSubTab(st.id); setExpandedWine(null); }} style={{
                    padding: "8px 18px", fontSize: 11, letterSpacing: 1, fontFamily: "'Outfit', sans-serif", fontWeight: wineSubTab === st.id ? 500 : 400,
                    background: wineSubTab === st.id ? COLORS.gold : COLORS.white, color: wineSubTab === st.id ? COLORS.bg : COLORS.textSecondary,
                    border: `1px solid ${wineSubTab === st.id ? COLORS.gold : COLORS.border}`, borderRadius: 6, cursor: "pointer", textTransform: "uppercase", transition: "all 0.2s",
                  }}>{st.label}</button>
                ))}
              </div>
            </div>

            {wineSubTab === "foundations" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ background: COLORS.greenSoft, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "18px 22px", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.gold, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 6, fontWeight: 500 }}>★ Why This Matters</div>
                  <p style={{ fontSize: 14, fontFamily: "'Outfit', sans-serif", color: COLORS.textSecondary, fontWeight: 300, margin: 0, lineHeight: 1.6 }}>
                    You don't need to be a sommelier — you need to speak about wine with quiet confidence. Learn the fundamentals here, then apply them to every bottle on our list.
                  </p>
                </div>
                {wineFoundations.map((sec) => (
                  <div key={sec.id} style={{ background: COLORS.white, border: `1px solid ${expandedWine === sec.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <button onClick={() => setExpandedWine(expandedWine === sec.id ? null : sec.id)} style={{
                      width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent",
                      border: "none", cursor: "pointer", color: COLORS.textPrimary, textAlign: "left",
                    }}>
                      <span style={{ fontSize: 18, fontWeight: 500 }}>{sec.title}</span>
                      <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedWine === sec.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                    </button>
                    {expandedWine === sec.id && (
                      <div style={{ padding: "0 22px 22px" }}>
                        <p style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontStyle: "italic", color: COLORS.textMuted, margin: "0 0 12px", lineHeight: 1.6 }}>{sec.intro}</p>
                        <div style={{ display: "grid", gap: 8 }}>
                          {sec.items.map((it, i) => (
                            <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px" }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold, marginBottom: 4 }}>{it.label}</div>
                              <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{it.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {wineSubTab === "wine" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ background: COLORS.greenSoft, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "18px 22px", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.gold, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 6, fontWeight: 500 }}>★ Selling Philosophy</div>
                  <p style={{ fontSize: 14, fontFamily: "'Outfit', sans-serif", color: COLORS.textSecondary, fontWeight: 300, margin: 0, lineHeight: 1.6 }}>
                    Recommend by taste, not by price. Never start a recommendation with the price. Start with the flavor, the pairing, and the story.
                  </p>
                </div>
                {wineProgram.map((sec) => (
                  <div key={sec.id} style={{ background: COLORS.white, border: `1px solid ${expandedWine === sec.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <button onClick={() => setExpandedWine(expandedWine === sec.id ? null : sec.id)} style={{
                      width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent",
                      border: "none", cursor: "pointer", color: COLORS.textPrimary, textAlign: "left",
                    }}>
                      <span style={{ fontSize: 18, fontWeight: 500 }}>{sec.title}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}>{sec.items.length} {sec.items.length === 1 ? "wine" : "wines"}</span>
                        <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedWine === sec.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                      </div>
                    </button>
                    {expandedWine === sec.id && (
                      <div style={{ padding: "0 22px 22px", display: "grid", gap: 8 }}>
                        {sec.items.map((w, i) => (
                          <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                              <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.gold }}>{w.name}</span>
                              <span style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.goldDim }}>{w.price}</span>
                            </div>
                            <div style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", fontWeight: 400, color: COLORS.textMuted, marginBottom: 4, letterSpacing: 0.5 }}>{w.region}</div>
                            <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{w.notes}</div>
                            <div style={{ marginTop: 6 }}>
                              <span style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: w.tier === "High End" ? COLORS.goldLight : w.tier === "Entry" ? COLORS.textMuted : COLORS.green, background: COLORS.greenSoft, padding: "2px 8px", borderRadius: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>{w.tier}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {wineSubTab === "spiritsFoundations" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ background: COLORS.greenSoft, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "18px 22px", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.gold, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 6, fontWeight: 500 }}>★ Why This Matters</div>
                  <p style={{ fontSize: 14, fontFamily: "'Outfit', sans-serif", color: COLORS.textSecondary, fontWeight: 300, margin: 0, lineHeight: 1.6 }}>
                    A spirit is defined by its base ingredient and how it's aged. Learn the categories here, then guide guests to the right pour with confidence.
                  </p>
                </div>
                {spiritsFoundations.map((sec) => (
                  <div key={sec.id} style={{ background: COLORS.white, border: `1px solid ${expandedWine === sec.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <button onClick={() => setExpandedWine(expandedWine === sec.id ? null : sec.id)} style={{
                      width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent",
                      border: "none", cursor: "pointer", color: COLORS.textPrimary, textAlign: "left",
                    }}>
                      <span style={{ fontSize: 18, fontWeight: 500 }}>{sec.title}</span>
                      <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedWine === sec.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                    </button>
                    {expandedWine === sec.id && (
                      <div style={{ padding: "0 22px 22px" }}>
                        <p style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, fontStyle: "italic", color: COLORS.textMuted, margin: "0 0 12px", lineHeight: 1.6 }}>{sec.intro}</p>
                        <div style={{ display: "grid", gap: 8 }}>
                          {sec.items.map((it, i) => (
                            <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px" }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold, marginBottom: 4 }}>{it.label}</div>
                              <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{it.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {wineSubTab === "spirits" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {spiritsProgram.map((cat) => (
                  <div key={cat.id} style={{ background: COLORS.white, border: `1px solid ${expandedWine === cat.id ? COLORS.goldLight : COLORS.border}`, borderRadius: 10, overflow: "hidden", transition: "border-color 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <button onClick={() => setExpandedWine(expandedWine === cat.id ? null : cat.id)} style={{
                      width: "100%", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "transparent",
                      border: "none", cursor: "pointer", color: COLORS.textPrimary, textAlign: "left",
                    }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 500 }}>{cat.title}</div>
                        <div style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textMuted, marginTop: 2 }}>{cat.desc}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}>{cat.items.length}</span>
                        <span style={{ fontSize: 16, color: COLORS.textMuted, transform: expandedWine === cat.id ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
                      </div>
                    </button>
                    {expandedWine === cat.id && (
                      <div style={{ padding: "0 22px 22px", display: "grid", gap: 8 }}>
                        {cat.items.map((s, i) => (
                          <div key={i} style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                              <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.gold }}>{s.name}</span>
                              <span style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: s.tier === "Ultra Premium" ? COLORS.goldLight : s.tier === "Top Shelf" ? COLORS.gold : s.tier === "Premium" ? COLORS.green : COLORS.textMuted, background: COLORS.greenSoft, padding: "2px 8px", borderRadius: 12, letterSpacing: 0.5, textTransform: "uppercase" }}>{s.tier}</span>
                            </div>
                            <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{s.profile}</div>
                            <div style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", fontWeight: 400, color: COLORS.textMuted, marginTop: 4 }}>Serve: {s.serve}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {wineSubTab === "cocktails" && (
              <div style={{ display: "grid", gap: 12 }}>
                {signatureCocktails.map((c, i) => (
                  <div key={i} style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "22px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20, fontWeight: 500, color: COLORS.gold }}>{c.name}</span>
                      <span style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", fontWeight: 400, color: COLORS.textMuted }}>{c.spirit}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 12 }}>
                      <div style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.gold, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Recipe</div>
                        <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{c.recipe}</div>
                      </div>
                      <div style={{ background: COLORS.accentBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: COLORS.gold, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Technique</div>
                        <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary, lineHeight: 1.6 }}>{c.technique}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                      <span style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}><span style={{ color: COLORS.goldDim }}>Glass:</span> {c.glass}</span>
                      <span style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}><span style={{ color: COLORS.goldDim }}>Pairs with:</span> {c.pairing}</span>
                    </div>
                    <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: `2px solid ${COLORS.goldDim}44`, fontSize: 13, fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", color: COLORS.textMuted, lineHeight: 1.5 }}>
                      "{c.verbiage}"
                    </div>
                  </div>
                ))}
              </div>
            )}

            {wineSubTab === "pairings" && (
              <div>
                <div style={{ marginBottom: 28 }}>
                  <h2 style={{ fontSize: 22, fontWeight: 400, color: COLORS.textPrimary, margin: "0 0 12px" }}>Wine & Menu Pairings</h2>
                  <div style={{ display: "grid", gap: 6 }}>
                    {winePairings.map((p, i) => (
                      <div key={i} style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px", display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 12, alignItems: "center" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold }}>{p.dish}</span>
                        <div>
                          <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", color: COLORS.goldDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Primary</div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary }}>{p.primary}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Alternative</div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textMuted }}>{p.alt}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 style={{ fontSize: 22, fontWeight: 400, color: COLORS.textPrimary, margin: "0 0 12px" }}>Cocktail & Menu Pairings</h2>
                  <div style={{ display: "grid", gap: 6 }}>
                    {cocktailPairings.map((p, i) => (
                      <div key={i} style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "14px 18px", display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 12, alignItems: "center" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.gold }}>{p.dish}</span>
                        <div>
                          <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", color: COLORS.goldDim, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Primary</div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textSecondary }}>{p.primary}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Alternative</div>
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textMuted }}>{p.alt}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* LANGUAGE GUIDE */}
        {activeTab === "language" && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>Reference Guide</div>
              <h1 style={{ fontSize: 34, fontWeight: 400, color: COLORS.textPrimary, margin: 0 }}>Approved Language</h1>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {languageStandards.map((ls, i) => (
                <div key={i} style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "22px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "grid", gridTemplateColumns: "90px 1fr", gap: "0 20px" }}>
                  <div style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: COLORS.gold, letterSpacing: 1.5, textTransform: "uppercase", paddingTop: 2 }}>{ls.moment}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.green, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, fontWeight: 500 }}>✓ Approved</div>
                      <div style={{ fontSize: 15, fontStyle: "italic", color: COLORS.textPrimary, fontWeight: 400, lineHeight: 1.5, paddingLeft: 12, borderLeft: `2px solid ${COLORS.greenLight}` }}>"{ls.approved}"</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.redLight, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, fontWeight: 500 }}>✗ Never Say</div>
                      <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", fontWeight: 300, color: COLORS.textMuted, lineHeight: 1.5, paddingLeft: 12, borderLeft: `2px solid ${COLORS.red}55` }}>{ls.never}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* QUIZ */}
        {activeTab === "quiz" && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: COLORS.goldDim, fontFamily: "'Outfit', sans-serif", textTransform: "uppercase", marginBottom: 8 }}>Training Assessment</div>
              <h1 style={{ fontSize: 34, fontWeight: 400, color: COLORS.textPrimary, margin: 0 }}>Knowledge Check</h1>
            </div>

            {!quizState.started ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <p style={{ fontSize: 15, color: COLORS.textSecondary, fontFamily: "'Outfit', sans-serif", fontWeight: 300, marginBottom: 28 }}>Select a quiz category to begin your assessment.</p>
                <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
                  {[
                    { label: "Service & Reception", type: "service", count: quizQuestions.filter(q => q.section === "Service" || q.section === "Reception").length },
                    { label: "Menu Knowledge", type: "menu", count: quizQuestions.filter(q => q.section === "Menu").length },
                    { label: "Wine & Spirits", type: "wine", count: quizQuestions.filter(q => q.section === "Wine & Spirits").length },
                    { label: "Full Assessment", type: "all", count: quizQuestions.length },
                  ].map((q) => (
                    <button key={q.type} onClick={() => startQuiz(q.type)} style={{
                      padding: "24px 32px", background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10,
                      cursor: "pointer", textAlign: "center", transition: "all 0.2s", minWidth: 190, boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                    }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.goldLight; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
                       onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; }}
                    >
                      <div style={{ fontSize: 17, fontWeight: 500, color: COLORS.textPrimary, marginBottom: 6 }}>{q.label}</div>
                      <div style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}>{q.count} questions in pool</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : quizState.finished ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ fontSize: 56, fontWeight: 300, color: quizState.score / quizState.pool.length >= 0.9 ? COLORS.goldLight : quizState.score / quizState.pool.length >= 0.7 ? COLORS.goldDim : COLORS.redLight, marginBottom: 8 }}>
                  {Math.round((quizState.score / quizState.pool.length) * 100)}%
                </div>
                <div style={{ fontSize: 20, fontWeight: 400, color: COLORS.textPrimary, marginBottom: 8 }}>{quizState.score} of {quizState.pool.length} correct</div>
                <div style={{ fontSize: 14, fontFamily: "'Outfit', sans-serif", color: COLORS.textSecondary, marginBottom: 28 }}>
                  {quizState.score / quizState.pool.length >= 0.9 ? "Excellent — you're ready for the floor." : quizState.score / quizState.pool.length >= 0.7 ? "Good foundation — review the areas you missed." : "Additional study required — review the training materials and try again."}
                </div>

                <div style={{ textAlign: "left", maxWidth: 680, margin: "0 auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
                  {quizState.pool.map((q, i) => {
                    const a = quizState.answers[i];
                    return (
                      <div key={i} style={{ background: a.correct ? COLORS.greenSoft : COLORS.redSoft, border: `1px solid ${a.correct ? COLORS.green + "33" : COLORS.red + "33"}`, borderRadius: 8, padding: "14px 18px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                          <span style={{ fontSize: 15, color: a.correct ? COLORS.green : COLORS.redLight }}>{a.correct ? "✓" : "✗"}</span>
                          <span style={{ fontSize: 10, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted, letterSpacing: 1.5, textTransform: "uppercase" }}>{q.section}</span>
                        </div>
                        <div style={{ fontSize: 14, color: COLORS.textPrimary, marginBottom: a.correct ? 0 : 6 }}>{q.q}</div>
                        {!a.correct && (
                          <div style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", color: COLORS.green, fontWeight: 400 }}>
                            Correct: {q.options[q.answer]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button onClick={() => setQuizState({ started: false, current: 0, score: 0, answers: [], finished: false, pool: [] })} style={{
                  padding: "12px 28px", background: COLORS.gold, border: "none", borderRadius: 7,
                  color: COLORS.bg, fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 500, cursor: "pointer",
                  letterSpacing: 1, textTransform: "uppercase",
                }}>Take Another Quiz</button>
              </div>
            ) : (
              <div style={{ maxWidth: 680, margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                  <span style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.goldDim, letterSpacing: 1.5, textTransform: "uppercase" }}>{quizState.pool[quizState.current].section}</span>
                  <span style={{ fontSize: 13, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted }}>{quizState.current + 1} / {quizState.pool.length}</span>
                </div>
                <div style={{ height: 3, background: COLORS.border, borderRadius: 2, marginBottom: 32 }}>
                  <div style={{ height: "100%", background: COLORS.gold, borderRadius: 2, width: `${((quizState.current) / quizState.pool.length) * 100}%`, transition: "width 0.4s ease" }} />
                </div>

                <h2 style={{ fontSize: 22, fontWeight: 400, color: COLORS.textPrimary, margin: "0 0 24px", lineHeight: 1.4 }}>{quizState.pool[quizState.current].q}</h2>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {quizState.pool[quizState.current].options.map((opt, i) => {
                    const answered = quizState.answers.length > quizState.current;
                    const isCorrect = i === quizState.pool[quizState.current].answer;
                    const wasChosen = answered && quizState.answers[quizState.current].chosen === i;
                    let bg = COLORS.white;
                    let borderColor = COLORS.border;
                    if (answered && isCorrect) { bg = COLORS.greenSoft; borderColor = COLORS.green + "66"; }
                    if (answered && wasChosen && !isCorrect) { bg = COLORS.redSoft; borderColor = COLORS.red + "66"; }

                    return (
                      <button key={`${quizState.current}-${i}`} onClick={() => !answered && answerQuestion(i)} disabled={answered} style={{
                        padding: "14px 18px", background: bg, border: `1px solid ${borderColor}`, borderRadius: 8,
                        cursor: answered ? "default" : "pointer", textAlign: "left", color: COLORS.textPrimary, fontSize: 15,
                        fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, transition: "all 0.2s", opacity: answered && !isCorrect && !wasChosen ? 0.45 : 1,
                      }} onMouseEnter={(e) => { if (!answered) { e.currentTarget.style.borderColor = COLORS.goldLight; e.currentTarget.style.background = COLORS.cardHover; } }}
                         onMouseLeave={(e) => { if (!answered) { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.background = COLORS.white; } }}
                      >
                        <span style={{ fontSize: 12, color: COLORS.textMuted, marginRight: 12, fontFamily: "'Outfit', sans-serif" }}>{String.fromCharCode(65 + i)}</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>

                {quizState.answers.length > quizState.current && !quizState.finished && (
                  <div style={{ marginTop: 20, textAlign: "right" }}>
                    <button onClick={nextQuestion} style={{
                      padding: "11px 24px", background: COLORS.gold, border: "none", borderRadius: 7,
                      color: COLORS.bg, fontSize: 12, fontFamily: "'Outfit', sans-serif", fontWeight: 500, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase",
                    }}>{quizState.current >= quizState.pool.length - 1 ? "See Results →" : "Next Question →"}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ASK A QUESTION */}
        {activeTab === "ask" && <AskTab />}
      </main>

      <footer style={{ borderTop: `1px solid ${COLORS.border}`, padding: "18px 24px", textAlign: "center", marginTop: 32 }}>
        <span style={{ fontSize: 11, fontFamily: "'Outfit', sans-serif", color: COLORS.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>Zuma Abu Dhabi — Al Maryah Island, Abu Dhabi — Confidential</span>
      </footer>
    </div>
  );
}

export default App;
